import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, LogOut, Users, Eye, Image as ImageIcon, MapPin, ExternalLink, Mail, ShieldCheck, History } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { emojiFor } from "@/data/issues";
import PledgeMap from "@/components/PledgeMap";
import DigestPanel from "@/components/DigestPanel";
import StateBreakdown from "@/components/StateBreakdown";
import AgeBreakdown from "@/components/AgeBreakdown";
import SiteFooter from "@/components/SiteFooter";
import AdminAccounts from "@/components/AdminAccounts";
import LoginLog from "@/components/LoginLog";
import { CollapsePanel } from "@/components/CollapsePanel";
import "@/voteforme.css";

const LOGO_WHITE = "/sbgwc-logo-white.png";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [pledges, setPledges] = useState([]);
  const [q, setQ] = useState("");
  const [range, setRange] = useState("all");

  useEffect(() => {
    api.get("/admin/stats", { params: { period: range } }).then(({ data }) => setStats(data)).catch((e) => console.warn("Stats failed", e));
    api.get("/admin/pledges", { params: { period: range } }).then(({ data }) => setPledges(data)).catch((e) => console.warn("Pledges failed", e));
  }, [range]);

  const RANGES = [["week", "This week"], ["month", "This month"], ["all", "All time"]];

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return pledges;
    return pledges.filter((p) =>
      (p.name || "").toLowerCase().includes(term) ||
      (p.email || "").toLowerCase().includes(term) ||
      (p.city || "").toLowerCase().includes(term) ||
      (p.state || "").toLowerCase().includes(term) ||
      (p.slug || "").toLowerCase().includes(term) ||
      (p.issues || []).join(" ").toLowerCase().includes(term) ||
      ((p.ip_location || {}).city || "").toLowerCase().includes(term) ||
      ((p.ip_location || {}).region || "").toLowerCase().includes(term)
    );
  }, [q, pledges]);

  const exportCsv = async () => {
    const { data } = await api.get("/admin/export", { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = "voteforme_submissions.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  const maxIssue = stats?.top_issues?.[0]?.count || 1;

  const locOf = (p) => {
    const gps = p.gps && p.gps.lat ? " 📍" : "";
    if (p.city || p.state) return [p.city, p.state].filter(Boolean).join(", ") + gps;
    const l = p.ip_location || {};
    const parts = [l.city, l.region].filter(Boolean);
    return (parts.join(", ") || (l.country || "—")) + gps;
  };

  return (
    <div className="vfm admin-wrap"><div className="vfm-bg-glow" />
      <div className="admin-header">
        <div className="admin-brand">
          <img src={LOGO_WHITE} alt="SBGWC" style={{ height: 36 }} />
          <div className="admin-title">Campaign Dashboard</div>
        </div>
        <div className="admin-actions">
          <button className="btn-yellow" onClick={exportCsv} style={{ width: "auto", padding: "10px 20px", fontSize: 14 }} data-testid="admin-export-csv-btn">
            <Download size={15} style={{ marginRight: 6, verticalAlign: "-3px" }} />Export CSV
          </button>
          <button className="btn-secondary" onClick={doLogout} data-testid="admin-logout-btn"><LogOut size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} />Logout</button>
        </div>
      </div>

      <div className="admin-body">
        <div className="range-bar">
          <span className="range-lbl">Showing</span>
          <div className="seg" role="tablist" data-testid="range-filter">
            {RANGES.map(([k, label]) => (
              <button key={k} className={range === k ? "on" : ""} onClick={() => setRange(k)} data-testid={`range-${k}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="stat-grid">
          <div className="stat-card"><div style={{ display: "flex", alignItems: "center", gap: 8 }}><Users size={18} color="#E02189" /><div className="num" data-testid="stat-pledges">{stats?.total_pledges ?? "—"}</div></div><div className="lbl">{range === "all" ? "Total Pledges" : "New Pledges"}</div></div>
          <div className="stat-card"><div style={{ display: "flex", alignItems: "center", gap: 8 }}><Eye size={18} color="#67DAD7" /><div className="num" data-testid="stat-views">{stats?.total_views ?? "—"}</div></div><div className="lbl">Page Views</div></div>
          <div className="stat-card"><div style={{ display: "flex", alignItems: "center", gap: 8 }}><ImageIcon size={18} color="#FDF71E" /><div className="num">{stats?.total_cards ?? "—"}</div></div><div className="lbl">Voice Cards Made</div></div>
          <div className="stat-card"><div style={{ display: "flex", alignItems: "center", gap: 8 }}><MapPin size={18} color="#F8D4DE" /><div className="num">{stats?.top_locations?.length ?? "—"}</div></div><div className="lbl">Locations Reached</div></div>
        </div>

        <PledgeMap range={range} />

        <div className="admin-two">
          <StateBreakdown byState={stats?.by_state} known={stats?.states_known ?? 0} total={stats?.total_pledges ?? 0} />
          <AgeBreakdown byAge={stats?.by_age} known={stats?.ages_known ?? 0} total={stats?.total_pledges ?? 0} />
        </div>

        <div className="admin-two">
          <div className="panel">
            <h3>Top Issues</h3>
            {(stats?.top_issues || []).slice(0, 10).map((it) => (
              <div className="bar-row" key={it.issue}>
                <span className="bl">{emojiFor(it.issue)} {it.issue}</span>
                <span className="bar-track"><span className="bar-fill" style={{ width: (it.count / maxIssue) * 100 + "%" }} /></span>
                <span className="bn">{it.count}</span>
              </div>
            ))}
            {(!stats?.top_issues || stats.top_issues.length === 0) && <div style={{ color: "rgba(255,255,255,.5)", fontSize: 13 }}>No data yet.</div>}
          </div>
          <div className="panel">
            <h3>Top Locations</h3>
            {(stats?.top_locations || []).map((it) => (
              <div className="bar-row" key={it.location}>
                <span className="bl" style={{ width: 190 }}>📍 {it.location}</span>
                <span className="bar-track"><span className="bar-fill" style={{ width: (it.count / (stats.top_locations[0].count || 1)) * 100 + "%", background: "linear-gradient(90deg,#67DAD7,#E02189)" }} /></span>
                <span className="bn">{it.count}</span>
              </div>
            ))}
            {(!stats?.top_locations || stats.top_locations.length === 0) && <div style={{ color: "rgba(255,255,255,.5)", fontSize: 13 }}>No location data yet.</div>}
          </div>
        </div>

        <div className="panel" style={{ marginTop: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
            <h3 style={{ margin: 0 }}>Submissions ({filtered.length})</h3>
            <input className="admin-search" placeholder="Search name, issue, location…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search" />
          </div>
          <div className="tbl-wrap">
            <table className="subs" data-testid="submissions-table">
              <thead>
                <tr><th>Name</th><th>Age</th><th>Issues</th><th>Location</th><th>Views</th><th>Card</th><th>Link</th><th>Date</th></tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} data-testid={`submission-row-${p.slug}`}>
                    <td style={{ fontWeight: 600 }}>{p.name}{p.email && <div className="tbl-email" data-testid={`pledge-email-${p.slug}`}>{p.email}</div>}{p.custom_message && <div className="tbl-quote" title={p.custom_message}>“{p.custom_message}”</div>}</td>
                    <td>{p.age || "—"}</td>
                    <td style={{ maxWidth: 240 }}>{(p.issues || []).map((i) => <span className="chip-sm" key={i}>{emojiFor(i)} {i}</span>)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{locOf(p)}</td>
                    <td style={{ color: "var(--joy-yellow)", fontWeight: 700 }}>{p.views}</td>
                    <td>{p.card_url ? <a className="tbl-link" href={`${API.replace("/api", "")}${p.card_url}`} target="_blank" rel="noopener noreferrer">img</a> : "—"}</td>
                    <td><a className="tbl-link" href={`/p/${p.slug}`} target="_blank" rel="noopener noreferrer">/p/{p.slug} <ExternalLink size={11} style={{ verticalAlign: "-1px" }} /></a></td>
                    <td style={{ whiteSpace: "nowrap", color: "rgba(255,255,255,.55)" }}>{(p.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", color: "rgba(255,255,255,.5)", padding: 30 }}>No submissions yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="admin-tools" data-testid="admin-tools">
          <CollapsePanel id="digest" title="Weekly Email Digest" icon={Mail}><DigestPanel /></CollapsePanel>
          <CollapsePanel id="accounts" title="Admin Accounts" icon={ShieldCheck}><AdminAccounts /></CollapsePanel>
          {user?.role === "owner" && <CollapsePanel id="login-log" title="Admin Login Activity" icon={History}><LoginLog /></CollapsePanel>}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
